import React, { useContext, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, ArrowRight, CheckCircle2 } from 'lucide-react';
import PublicHeader, { NO_LINKS, HOME } from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';
import AuthContext from '../context/AuthContext';
// Live store snapshots (scroll on hover) — shown as REAL stores by trade, never
// by template codename: we sell outcomes, not an inventory of designs.
import { SNAP as snapUrl } from '../utils/brandAssets';
import { firstJobRoute, needsSetup } from '../utils/businessConfig';

const SHOWCASE = [
  { id: 16, trade: 'Bridal jewellery' },
  { id: 9, trade: 'Fine jewellery' },
  { id: 18, trade: 'Streetwear' },
];

// Every claim here is true today: there is NO commission of any kind (revenue
// is the subscription — see "The money model" in CLAUDE.md). The old "keep 97%
// of every sale" line implied a 3% cut that nothing charges; never bring it back.
const PROMISES = [
  '0% commission — every sale is fully yours',
  'Live in under 30 minutes',
  'Money settles to your own bank',
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { loginUser, user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();

  if (user) {
    // If kicked here via a 401 interceptor with ?redirect=<orig path>, restore
    // the user there post-login. Only same-origin paths (open-redirect guard).
    const redirect = searchParams.get('redirect');
    const safeRedirect =
      redirect && redirect.startsWith('/') && !redirect.startsWith('//')
        ? redirect
        // A brand-new business that hasn't picked its features yet is sent to
        // the setup page, not the dashboard.
        : needsSetup() ? '/setup'
        // no explicit redirect → the business's chosen first job (fashion = /homepage).
        : firstJobRoute();
    return <Navigate to={safeRedirect} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await loginUser(e);
    } catch (err) {
      setError('Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fieldWrap =
    'flex items-center gap-3 rounded-xl border-[1.5px] border-line bg-bone/60 px-4 h-[52px] transition-colors focus-within:border-ink focus-within:bg-white';
  const inputCls =
    'w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-gray-400';
  const labelCls =
    'block font-display text-[11px] font-bold uppercase tracking-[0.14em] text-muted';

  return (
    <div className="min-h-screen overflow-x-hidden bg-bone text-ink">
      {/* Auth pages get the header with NO marketing nav — the only thing
          someone on this page wants from a nav is a way out. */}
      <PublicHeader links={NO_LINKS} secondary={HOME} cta={{ label: 'Sign up free', href: '/Register' }} ctaTone="quiet" />

      <div className="mx-auto grid min-h-[calc(100vh-66px)] max-w-7xl grid-cols-1 items-stretch gap-0 px-4 sm:px-6 lg:grid-cols-[1fr_1.05fr] lg:gap-14 lg:px-8">

        {/* ───────────── Left: welcome + form ───────────── */}
        <div className="flex items-center justify-center py-12 lg:py-0">
          <motion.div
            className="w-full max-w-md"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-display text-[11px] font-bold uppercase tracking-[0.22em] text-primary">
              Inikru
            </p>
            <h1 className="mt-4 font-serif text-[3.1rem] font-medium leading-[1.02] tracking-[-0.01em] text-ink sm:text-[3.6rem]">
              Welcome back.
            </h1>
            <p className="mt-4 text-[15.5px] leading-relaxed text-muted">
              Your store, orders and money — all in one place.
            </p>

            <div className="mt-9 rounded-2xl border-[1.5px] border-line bg-white p-6 shadow-[0_24px_48px_-28px_rgba(27,27,27,0.18)] sm:p-8">
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-5 rounded-xl border-[1.5px] border-primary/25 bg-primary-light px-4 py-3 text-[13.5px] font-semibold text-primary-dark"
                  role="alert"
                >
                  {error}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className={`mb-2 ${labelCls}`}>
                    Email address
                  </label>
                  <div className={fieldWrap}>
                    <Mail size={18} className="flex-shrink-0 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      id="email"
                      placeholder="you@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      className={inputCls}
                    />
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label htmlFor="password" className={labelCls}>
                      Password
                    </label>
                    <Link
                      to="/forgot-password"
                      className="rounded text-[13px] font-semibold text-primary hover:text-primary-dark focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    >
                      Forgot?
                    </Link>
                  </div>
                  <div className={fieldWrap}>
                    <Lock size={18} className="flex-shrink-0 text-gray-400" />
                    <input
                      type={passwordVisible ? 'text' : 'password'}
                      name="password"
                      id="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      className={inputCls}
                    />
                    <button
                      type="button"
                      onClick={() => setPasswordVisible((v) => !v)}
                      className="rounded text-gray-400 transition-colors hover:text-ink focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                      aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                    >
                      {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <label className="flex cursor-pointer items-center gap-2 text-[13.5px] font-semibold text-muted">
                  <input type="checkbox" name="remember-me" className="h-4 w-4 rounded accent-[#B40A0A]" />
                  Keep me signed in
                </label>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-primary font-display text-[14.5px] font-bold text-white transition-colors hover:bg-primary-dark disabled:opacity-60 focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  {isLoading ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      Signing in…
                    </>
                  ) : (
                    <>
                      Sign in <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
              </form>

              <div className="my-6 flex items-center gap-3">
                <span className="h-px flex-1 bg-line" />
                <span className="text-[12.5px] font-semibold text-muted">New to Inikru?</span>
                <span className="h-px flex-1 bg-line" />
              </div>

              <Link
                to="/Register"
                className="flex h-[50px] w-full items-center justify-center gap-2 rounded-xl border-[1.5px] border-ink bg-white font-display text-[14px] font-bold text-ink transition-colors hover:bg-ink hover:text-white focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                Open your store free
              </Link>
            </div>

            <p className="mt-6 text-center text-[13px] text-muted">
              Need help?{' '}
              <a href="mailto:team@inikru.com" className="font-semibold text-primary hover:text-primary-dark">
                team@inikru.com
              </a>{' '}
              · +91 97043 87878
            </p>
          </motion.div>
        </div>

        {/* ───────────── Right: stores running on Inikru (desktop) ───────────── */}
        <div className="relative hidden lg:flex">
          <motion.div
            className="relative my-10 flex w-full items-center overflow-hidden rounded-2xl bg-ink"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Brand accent: a single crimson thread across the top. */}
            <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-primary" />
            {/* A faint oversized serif mark — texture, not a message. */}
            <span
              aria-hidden
              className="pointer-events-none absolute -right-8 -bottom-24 select-none font-serif text-[19rem] font-medium leading-none text-white opacity-[0.045]"
            >
              in
            </span>

            <div className="relative z-10 w-full px-12 py-14">
              <p className="font-display text-[11px] font-bold uppercase tracking-[0.22em] text-cream/70">
                Stores running on Inikru
              </p>
              <h2 className="mt-5 max-w-md font-serif text-[2.6rem] font-medium leading-[1.1] tracking-[-0.01em] text-white">
                Every kind of business, at its own address.
              </h2>
              <p className="mt-4 max-w-md text-[15px] leading-relaxed text-gray-400">
                Your website, your payments, your customers — run from one place.
              </p>

              <div className="mt-10 grid grid-cols-3 gap-4">
                {SHOWCASE.map((s, i) => (
                  <motion.div
                    key={s.id}
                    className="group overflow-hidden rounded-xl border border-white/12 bg-white/5"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <div
                      className="h-44 w-full bg-top bg-no-repeat transition-[background-position] duration-[5000ms] ease-linear group-hover:bg-bottom"
                      style={{ backgroundImage: `url(${snapUrl(s.id)})`, backgroundSize: '100% auto' }}
                      aria-label={`A ${s.trade.toLowerCase()} store built on Inikru`}
                      role="img"
                    />
                    <div className="border-t border-white/10 px-3 py-3">
                      <span className="font-display text-[12px] font-bold uppercase tracking-[0.08em] text-cream">
                        {s.trade}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-10 space-y-3 border-t border-white/10 pt-8">
                {PROMISES.map((t) => (
                  <p key={t} className="flex items-center gap-2.5 text-[14px] font-semibold text-white">
                    <CheckCircle2 className="h-[18px] w-[18px] flex-shrink-0 text-cream/80" />
                    {t}
                  </p>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default Login;
